import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NORWEGIAN_REGIONS } from '../lib/regions';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, FileSignature, Edit, Edit2, Calendar, Pause, Play, Ban, Power, Wrench, FileText } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { InstallationForm } from './InstallationForm';
import NextServiceNote from '../components/NextServiceNote';
import { rowKey } from '../lib/rowKey';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

export const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'system_admin';
  const canEditCleaning = ['admin', 'system_admin', 'tekniker'].includes(user?.role);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [services, setServices] = useState([]);
  const [cleaningCycle, setCleaningCycle] = useState(null);
  const [loading, setLoading] = useState(true);

  const [productDialog, setProductDialog] = useState(false);
  const [equipmentDialog, setEquipmentDialog] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [editCycleDialog, setEditCycleDialog] = useState(false);
  const [editServiceDialog, setEditServiceDialog] = useState(false);
  const [editProductDialog, setEditProductDialog] = useState(false);
  const [editEquipmentDialog, setEditEquipmentDialog] = useState(false);

  const [newProduct, setNewProduct] = useState({ name: '', type: '' });
  const [newEquipment, setNewEquipment] = useState({ name: '', serial_number: '', installation_date: '' });
  const [editProductData, setEditProductData] = useState({ id: '', name: '', type: '' });
  const [editEquipmentData, setEditEquipmentData] = useState({ id: '', name: '', serial_number: '', installation_date: '' });
  const [newService, setNewService] = useState({ service_type: 'rens', signature_name: '', notes: '', equipment: [], is_urgent: false });
  const [resolveNote, setResolveNote] = useState(true);
  const [editCycleData, setEditCycleData] = useState({ week: '', year: '' });
  const [editNextDialog, setEditNextDialog] = useState(false);
  const [editNextData, setEditNextData] = useState({ week: '', year: '' });
  const [editServiceData, setEditServiceData] = useState({ id: '', date: '', time: '' });
  const [pauseDialog, setPauseDialog] = useState(false);
  const [pauseWeeks, setPauseWeeks] = useState(4);
  const [editCustomerDialog, setEditCustomerDialog] = useState(false);
  const [deactivateCustomerDialog, setDeactivateCustomerDialog] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [deleteEquipmentId, setDeleteEquipmentId] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [editInstallationId, setEditInstallationId] = useState(null);
  const [editCustomerData, setEditCustomerData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    postal_code: '',
    region: '',
    notes: '',
    door_code: '',
    gate_code: '',
    alarm_code: '',
    access_info: ''
  });
  const [equipmentNames, setEquipmentNames] = useState([]);

  const signaturePad = useRef(null);

  useEffect(() => {
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the customer id changes
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const [customerRes, productsRes, equipmentRes, servicesRes] = await Promise.all([
        axios.get(`${API}/customers/${id}`),
        axios.get(`${API}/customers/${id}/products`),
        axios.get(`${API}/customers/${id}/equipment`),
        axios.get(`${API}/customers/${id}/services`),
      ]);

      setCustomer(customerRes.data);
      setProducts(productsRes.data);
      setEquipment(equipmentRes.data);
      setServices(servicesRes.data);

      try {
        const eqRes = await axios.get(`${API}/equipment-catalog`);
        setEquipmentNames(eqRes.data || []);
      } catch (err) { console.warn('[CustomerDetail]', err); }

      try {
        const cycleRes = await axios.get(`${API}/cleaning-cycles/${id}`);
        setCleaningCycle(cycleRes.data);
      } catch (error) {
        console.log('No cleaning cycle found');
      }

      try {
        const instRes = await axios.get(`${API}/installations?customer_id=${id}`);
        setInstallations(instRes.data);
      } catch (err) { console.warn('[CustomerDetail]', err); }
    } catch (error) {
      toast.error('Kunne ikke hente kundedata');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customers/${id}/products`, newProduct);
      toast.success('Produkt lagt til!');
      setProductDialog(false);
      setNewProduct({ name: '', type: '' });
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke legge til produkt');
    }
  };

  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customers/${id}/equipment`, {
        ...newEquipment,
        installation_date: new Date(newEquipment.installation_date).toISOString()
      });
      toast.success('Utstyr lagt til!');
      setEquipmentDialog(false);
      setNewEquipment({ name: '', serial_number: '', installation_date: '' });
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke legge til utstyr');
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      const signatureData = signaturePad.current?.toDataURL() || null;
      
      // Only send equipment if it's an installation and equipment is not empty
      const equipmentData = newService.service_type === 'installasjon' && newService.equipment.length > 0 
        ? newService.equipment.filter(eq => eq.name && eq.serial_number) 
        : null;
      
      await axios.post(`${API}/services`, {
        customer_id: id,
        service_type: newService.service_type,
        signature_name: newService.signature_name || null,
        notes: newService.notes || null,
        signature_data: signatureData,
        equipment: equipmentData,
        is_urgent: newService.is_urgent,
        resolve_next_note: !!customer.next_service_note && resolveNote
      });
      toast.success('Service registrert!');
      setServiceDialog(false);
      setNewService({ service_type: 'rens', signature_name: '', notes: '', equipment: [], is_urgent: false });
      if (signaturePad.current) {
        signaturePad.current.clear();
      }
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke registrere service');
    }
  };

  const addEquipmentField = () => {
    setNewService({
      ...newService,
      equipment: [...newService.equipment, { _key: rowKey(), name: '', serial_number: '' }]
    });
  };

  const removeEquipmentField = (index) => {
    const updated = newService.equipment.filter((_, i) => i !== index);
    setNewService({ ...newService, equipment: updated });
  };

  const updateEquipmentField = (index, field, value) => {
    const updated = [...newService.equipment];
    updated[index] = { ...updated[index], [field]: value };
    setNewService({ ...newService, equipment: updated });
  };

  const handleDeleteProduct = async () => {
    if (!deleteProductId) return;
    try {
      await axios.delete(`${API}/products/${deleteProductId}`);
      toast.success('Produkt slettet');
      setDeleteProductId(null);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke slette produkt');
      setDeleteProductId(null);
    }
  };

  const handleDeleteEquipment = async () => {
    if (!deleteEquipmentId) return;
    try {
      await axios.delete(`${API}/equipment/${deleteEquipmentId}`);
      toast.success('Utstyr slettet');
      setDeleteEquipmentId(null);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke slette utstyr');
      setDeleteEquipmentId(null);
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/products/${editProductData.id}`, {
        name: editProductData.name,
        type: editProductData.type
      });
      toast.success('Produkt oppdatert!');
      setEditProductDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere produkt');
    }
  };

  const handleEditEquipment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/equipment/${editEquipmentData.id}`, {
        name: editEquipmentData.name,
        serial_number: editEquipmentData.serial_number,
        installation_date: new Date(editEquipmentData.installation_date).toISOString()
      });
      toast.success('Utstyr oppdatert!');
      setEditEquipmentDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere utstyr');
    }
  };

  const handleUpdateInterval = async (newInterval) => {
    try {
      await axios.put(`${API}/cleaning-cycles/${id}`, { interval_weeks: parseInt(newInterval) });
      toast.success('Intervall oppdatert!');
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere intervall');
    }
  };

  const handleUpdateLastCleaned = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/cleaning-cycles/${id}`, {
        last_cleaned_week: parseInt(editCycleData.week),
        last_cleaned_year: parseInt(editCycleData.year)
      });
      toast.success('Siste rens dato oppdatert!');
      setEditCycleDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere dato');
    }
  };

  const handleUpdateNextDue = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/cleaning-cycles/${id}`, {
        next_due_week: parseInt(editNextData.week),
        next_due_year: parseInt(editNextData.year)
      });
      toast.success('Neste rensuke oppdatert!');
      setEditNextDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere rensuke');
    }
  };

  const handleUpdateServiceDate = async (e) => {
    e.preventDefault();
    try {
      const dateTime = new Date(`${editServiceData.date}T${editServiceData.time}`);
      await axios.put(`${API}/services/${editServiceData.id}`, null, {
        params: { completed_at: dateTime.toISOString() }
      });
      toast.success('Service dato oppdatert!');
      setEditServiceDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere service dato');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne service-oppføringen?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service slettet');
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke slette service');
    }
  };

  const handlePauseCycle = async () => {
    try {
      const now = new Date();
      const currentWeek = getISOWeek(now);
      const currentYear = now.getFullYear();
      
      let pauseUntilWeek = currentWeek + pauseWeeks;
      let pauseUntilYear = currentYear;
      
      while (pauseUntilWeek > 52) {
        pauseUntilWeek -= 52;
        pauseUntilYear += 1;
      }
      
      await axios.put(`${API}/cleaning-cycles/${id}`, {
        paused_until_week: pauseUntilWeek,
        paused_until_year: pauseUntilYear
      });
      toast.success(`Rens pauset til uke ${pauseUntilWeek}, ${pauseUntilYear}`);
      setPauseDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke pause rens');
    }
  };

  const handleResumeCycle = async () => {
    try {
      await axios.put(`${API}/cleaning-cycles/${id}`, {
        paused_until_week: 0,
        paused_until_year: 0
      });
      toast.success('Rens gjenopptatt');
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke gjenoppta rens');
    }
  };

  const handleToggleActive = async () => {
    try {
      const newStatus = !cleaningCycle?.is_active;
      await axios.put(`${API}/cleaning-cycles/${id}`, {
        is_active: newStatus
      });
      toast.success(newStatus ? 'Rens aktivert' : 'Rens deaktivert');
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke endre status');
    }
  };

  const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/customers/${id}`, editCustomerData);
      toast.success('Kundeinformasjon oppdatert!');
      setEditCustomerDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere kunde');
    }
  };

  const handleToggleCustomerActive = async () => {
    try {
      const res = await axios.put(`${API}/customers/${id}/toggle-active`);
      toast.success(res.data.is_active ? 'Kunde aktivert' : 'Kunde deaktivert');
      setDeactivateCustomerDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Kunne ikke endre kundestatus');
    }
  };

  const clearSignature = () => {
    if (signaturePad.current) {
      signaturePad.current.clear();
    }
  };

  const openInstallationPdf = async (instId) => {
    try {
      const response = await axios.get(`${API}/installations/${instId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `montering_${instId}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      toast.success('Monterings-PDF lastet ned');
    } catch (error) {
      toast.error('Kunne ikke laste ned PDF. Prøv igjen.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Kunde ikke funnet</p>
        <Button onClick={() => navigate('/customers')} className="mt-4">
          Tilbake til kunder
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <datalist id="equipment-name-options">
        {equipmentNames.map((n) => <option key={n} value={n} />)}
      </datalist>
      <Button
        variant="ghost"
        onClick={() => navigate('/customers')}
        className="mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Tilbake
      </Button>

      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {customer.name}
            </h1>
            <p className="text-slate-600">Kundedetaljer og servicehistorikk</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'system_admin') && (
            <Button
              variant="outline"
              size="sm"
              data-testid="toggle-customer-active-button"
              onClick={() => {
                if (customer.is_active === false) {
                  handleToggleCustomerActive();
                } else {
                  setDeactivateCustomerDialog(true);
                }
              }}
              className={customer.is_active === false ? 'text-brand-600 border-brand-300 hover:bg-brand-50' : 'text-slate-500 border-slate-300 hover:bg-slate-50'}
            >
              <Power className="h-4 w-4 mr-1.5" />
              {customer.is_active === false ? 'Aktiver kunde' : 'Deaktiver kunde'}
            </Button>
          )}
        </div>
        {customer.is_active === false && (
          <div className="mt-3 p-3 bg-slate-100 border border-slate-300 rounded-lg flex items-center gap-2" data-testid="customer-inactive-banner">
            <Power className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-600 font-medium">Denne kunden er deaktivert og vises ikke i rensplanlegging eller dashboard.</span>
          </div>
        )}
        <div className="mt-3">
          <NextServiceNote customerId={customer.id} note={customer.next_service_note} onSaved={(n) => setCustomer({ ...customer, next_service_note: n })} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hurtighandlinger</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Dialog open={serviceDialog} onOpenChange={(o) => { setServiceDialog(o); if (o) setResolveNote(true); }}>
                <DialogTrigger asChild>
                  <Button data-testid="register-service-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                    <FileSignature className="mr-2 h-4 w-4" />
                    Registrer Service
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Registrer service</DialogTitle>
                    <DialogDescription>Fyll inn serviceinformasjon. Ditt navn vil automatisk bli registrert som tekniker.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateService} className="flex flex-col flex-1 overflow-hidden">
                    <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                      {customer.next_service_note && (
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 space-y-2" data-testid="service-next-note">
                          <p className="text-sm text-amber-900"><strong>Til neste service:</strong> {customer.next_service_note}</p>
                          <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                            <input type="checkbox" checked={resolveNote} onChange={(e) => setResolveNote(e.target.checked)} className="h-4 w-4" data-testid="service-resolve-note" />
                            Merknaden er utført (fjernes og lagres i historikken)
                          </label>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Type *</Label>
                        <Select
                          value={newService.service_type}
                          onValueChange={(value) => setNewService({ ...newService, service_type: value })}
                        >
                          <SelectTrigger data-testid="service-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="rens">Rens</SelectItem>
                            <SelectItem value="installasjon">Installasjon</SelectItem>
                            <SelectItem value="reparasjon">Reparasjon</SelectItem>
                            <SelectItem value="vedlikehold">Vedlikehold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signature_name">Navn (for bekreftelse)</Label>
                        <Input
                          id="signature_name"
                          data-testid="signature-name-input"
                          value={newService.signature_name}
                          onChange={(e) => setNewService({ ...newService, signature_name: e.target.value })}
                          placeholder="Valgfritt - navn på person som bekrefter"
                        />
                        <p className="text-xs text-slate-500">Du ({user?.name}) vil automatisk bli registrert som tekniker</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Signatur (valgfri)</Label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors relative">
                          <SignatureCanvas
                            ref={signaturePad}
                            canvasProps={{
                              className: 'signature-canvas w-full h-40',
                              'data-testid': 'signature-canvas'
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={clearSignature}
                          data-testid="clear-signature-button"
                        >
                          Tøm signatur
                        </Button>
                      </div>
                      {newService.service_type === 'installasjon' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Utstyr (valgfri)</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addEquipmentField}
                              data-testid="add-equipment-field"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Legg til utstyr
                            </Button>
                          </div>
                          {newService.equipment.map((eq, index) => (
                            <div key={eq._key || index} className="p-3 border border-slate-200 rounded-lg space-y-2 bg-slate-50">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-slate-700">Utstyr {index + 1}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeEquipmentField(index)}
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                  data-testid={`remove-equipment-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div>
                                <Label htmlFor={`eq-name-${index}`} className="text-xs">Utstyrsnavn</Label>
                                <Input
                                  id={`eq-name-${index}`}
                                  data-testid={`equipment-name-${index}`}
                                  value={eq.name}
                                  onChange={(e) => updateEquipmentField(index, 'name', e.target.value)}
                                  placeholder="F.eks. Tappetårn"
                                  className="mt-1"
                                  list="equipment-name-options"
                                />
                              </div>
                              <div>
                                <Label htmlFor={`eq-serial-${index}`} className="text-xs">Serienummer</Label>
                                <Input
                                  id={`eq-serial-${index}`}
                                  data-testid={`equipment-serial-${index}`}
                                  value={eq.serial_number}
                                  onChange={(e) => updateEquipmentField(index, 'serial_number', e.target.value)}
                                  placeholder="Serienummer"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <input
                          type="checkbox"
                          id="is_urgent"
                          data-testid="is-urgent-checkbox"
                          checked={newService.is_urgent}
                          onChange={(e) => setNewService({ ...newService, is_urgent: e.target.checked })}
                          className="h-5 w-5 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                        />
                        <Label htmlFor="is_urgent" className="flex-1 cursor-pointer">
                          <span className="font-medium text-orange-800">Akutt oppdrag</span>
                          <p className="text-xs text-orange-600 mt-0.5">Marker dette som et hasteoppdrag som krever umiddelbar oppmerksomhet</p>
                        </Label>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notater</Label>
                        <Textarea
                          id="notes"
                          data-testid="notes-textarea"
                          value={newService.notes}
                          onChange={(e) => setNewService({ ...newService, notes: e.target.value })}
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-slate-200 bg-white">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setServiceDialog(false)}
                        className="flex-1"
                        data-testid="cancel-service-button"
                      >
                        Avbryt
                      </Button>
                      <Button type="submit" data-testid="submit-service-button" className="flex-1 btn-primary bg-slate-900 hover:bg-slate-800">
                        Registrer
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Kontaktinformasjon</CardTitle>
                <Dialog open={editCustomerDialog} onOpenChange={setEditCustomerDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditCustomerData({
                        name: customer.name,
                        contact_person: customer.contact_person,
                        phone: customer.phone,
                        email: customer.email || '',
                        address: customer.address,
                        postal_code: customer.postal_code || '',
                        region: customer.region || '',
                        notes: customer.notes || '',
                        door_code: customer.door_code || '',
                        gate_code: customer.gate_code || '',
                        alarm_code: customer.alarm_code || '',
                        access_info: customer.access_info || ''
                      })}
                      data-testid="edit-customer-button"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Rediger kundeinformasjon</DialogTitle>
                      <DialogDescription>Oppdater kontaktinformasjon og notater</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditCustomer} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_name">Firmanavn *</Label>
                        <Input
                          id="edit_name"
                          data-testid="edit-customer-name"
                          value={editCustomerData.name}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_contact">Kontaktperson *</Label>
                        <Input
                          id="edit_contact"
                          data-testid="edit-customer-contact"
                          value={editCustomerData.contact_person}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, contact_person: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_phone">Telefon *</Label>
                        <Input
                          id="edit_phone"
                          data-testid="edit-customer-phone"
                          value={editCustomerData.phone}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, phone: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_email">E-post</Label>
                        <Input
                          id="edit_email"
                          data-testid="edit-customer-email"
                          type="email"
                          value={editCustomerData.email}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_address">Adresse *</Label>
                        <Input
                          id="edit_address"
                          data-testid="edit-customer-address"
                          value={editCustomerData.address}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, address: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_postal_code">Postnummer</Label>
                        <Input
                          id="edit_postal_code"
                          data-testid="edit-customer-postal-code"
                          value={editCustomerData.postal_code}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, postal_code: e.target.value })}
                          placeholder="0000"
                          maxLength={4}
                        />
                        <p className="text-xs text-slate-500">Brukes for å sortere kunder etter område i renselisten</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_region">Område</Label>
                        <Input
                          id="edit_region"
                          data-testid="edit-customer-region"
                          list="region-options"
                          value={editCustomerData.region}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, region: e.target.value })}
                          placeholder="F.eks. Oslo, Innlandet, Østfold"
                        />
                        <datalist id="region-options">
                          {NORWEGIAN_REGIONS.map((r) => <option key={r} value={r} />)}
                        </datalist>
                        <p className="text-xs text-slate-500">Velg fylke eller skriv eget område. Søkbart i renselisten.</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-2">
                          <Label htmlFor="edit_door_code">Dørkode</Label>
                          <Input
                            id="edit_door_code"
                            data-testid="edit-customer-door-code"
                            value={editCustomerData.door_code}
                            onChange={(e) => setEditCustomerData({ ...editCustomerData, door_code: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit_gate_code">Portkode</Label>
                          <Input
                            id="edit_gate_code"
                            data-testid="edit-customer-gate-code"
                            value={editCustomerData.gate_code}
                            onChange={(e) => setEditCustomerData({ ...editCustomerData, gate_code: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit_alarm_code">Alarmkode</Label>
                          <Input
                            id="edit_alarm_code"
                            data-testid="edit-customer-alarm-code"
                            value={editCustomerData.alarm_code}
                            onChange={(e) => setEditCustomerData({ ...editCustomerData, alarm_code: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_access_info">Tilgangsinfo / nøkkelinfo</Label>
                        <Textarea
                          id="edit_access_info"
                          data-testid="edit-customer-access-info"
                          value={editCustomerData.access_info}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, access_info: e.target.value })}
                          rows={2}
                          placeholder="F.eks. nøkkelboks-plassering, hvor man henvender seg, etc."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_notes">Notater</Label>
                        <Textarea
                          id="edit_notes"
                          data-testid="edit-customer-notes"
                          value={editCustomerData.notes}
                          onChange={(e) => setEditCustomerData({ ...editCustomerData, notes: e.target.value })}
                          rows={3}
                          placeholder="Notater om kunden..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditCustomerDialog(false)}
                          className="flex-1"
                        >
                          Avbryt
                        </Button>
                        <Button type="submit" data-testid="submit-edit-customer" className="flex-1 bg-slate-900 hover:bg-slate-800">
                          Lagre
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Kontaktperson:</span>
                  <p className="text-slate-600">{customer.contact_person}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Telefon:</span>
                  <p className="text-slate-600">{customer.phone}</p>
                </div>
                {customer.email && (
                  <div>
                    <span className="font-medium text-slate-700">E-post:</span>
                    <p className="text-slate-600">{customer.email}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-slate-700">Adresse:</span>
                  <p className="text-slate-600">{customer.address}{customer.postal_code ? `, ${customer.postal_code}` : ''}</p>
                </div>
                {customer.region && (
                  <div data-testid="customer-region">
                    <span className="font-medium text-slate-700">Område:</span>
                    <p className="text-slate-600">{customer.region}</p>
                  </div>
                )}
                {(customer.door_code || customer.gate_code || customer.alarm_code) && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="customer-codes">
                    {customer.door_code && (
                      <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">Dør: <strong>{customer.door_code}</strong></span>
                    )}
                    {customer.gate_code && (
                      <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">Port: <strong>{customer.gate_code}</strong></span>
                    )}
                    {customer.alarm_code && (
                      <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">Alarm: <strong>{customer.alarm_code}</strong></span>
                    )}
                  </div>
                )}
                {customer.access_info && (
                  <div>
                    <span className="font-medium text-slate-700">Tilgangsinfo:</span>
                    <p className="text-slate-600 whitespace-pre-wrap">{customer.access_info}</p>
                  </div>
                )}
              </div>
              {customer.notes && (
                <div className="pt-3 border-t border-slate-200">
                  <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Notater:</span>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{customer.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Rens Syklus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {cleaningCycle ? (
              <>
                <div>
                  <span className="text-sm font-medium text-slate-700">Status:</span>
                  <div className="flex items-center gap-2 mt-1">
                    {cleaningCycle.is_active === false ? (
                      <span className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded">Deaktivert</span>
                    ) : cleaningCycle.paused_until_week ? (
                      <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded">
                        Pauset til uke {cleaningCycle.paused_until_week}, {cleaningCycle.paused_until_year}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-brand-100 text-brand-800 rounded">Aktiv</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700">Intervall:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <Select
                      defaultValue={cleaningCycle.interval_weeks.toString()}
                      onValueChange={handleUpdateInterval}
                      disabled={cleaningCycle.is_active === false}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 uker</SelectItem>
                        <SelectItem value="4">4 uker</SelectItem>
                        <SelectItem value="6">6 uker</SelectItem>
                        <SelectItem value="8">8 uker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Sist renset:</span>
                    {canEditCleaning && (
                      <Dialog open={editCycleDialog} onOpenChange={setEditCycleDialog}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="edit-last-cleaned-button"
                            onClick={() => setEditCycleData({
                              week: cleaningCycle.last_cleaned_week.toString(),
                              year: cleaningCycle.last_cleaned_year.toString()
                            })}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Endre siste rens dato</DialogTitle>
                            <DialogDescription>Oppdater når kunden sist ble renset</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleUpdateLastCleaned} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit_week">Uke nummer (1-52) *</Label>
                              <Input
                                id="edit_week"
                                data-testid="edit-week-input"
                                type="number"
                                min="1"
                                max="52"
                                value={editCycleData.week}
                                onChange={(e) => setEditCycleData({ ...editCycleData, week: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit_year">År *</Label>
                              <Input
                                id="edit_year"
                                data-testid="edit-year-input"
                                type="number"
                                min="2020"
                                max="2030"
                                value={editCycleData.year}
                                onChange={(e) => setEditCycleData({ ...editCycleData, year: e.target.value })}
                                required
                              />
                            </div>
                            <Button type="submit" data-testid="submit-edit-cycle-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                              Oppdater
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  <p className="text-slate-600">Uke {cleaningCycle.last_cleaned_week}, {cleaningCycle.last_cleaned_year}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Neste rens:</span>
                    {canEditCleaning && (
                      <Dialog open={editNextDialog} onOpenChange={setEditNextDialog}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="edit-next-due-button"
                            disabled={cleaningCycle.is_active === false}
                            onClick={() => setEditNextData({
                              week: cleaningCycle.next_due_week.toString(),
                              year: cleaningCycle.next_due_year.toString()
                            })}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            <span className="text-xs">Endre rensuke</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Endre neste rensuke</DialogTitle>
                            <DialogDescription>Flytt neste planlagte rens til en bestemt uke</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleUpdateNextDue} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="next_week">Uke nummer (1-52) *</Label>
                              <Input
                                id="next_week"
                                data-testid="edit-next-week-input"
                                type="number"
                                min="1"
                                max="52"
                                value={editNextData.week}
                                onChange={(e) => setEditNextData({ ...editNextData, week: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="next_year">År *</Label>
                              <Input
                                id="next_year"
                                data-testid="edit-next-year-input"
                                type="number"
                                min="2020"
                                max="2030"
                                value={editNextData.year}
                                onChange={(e) => setEditNextData({ ...editNextData, year: e.target.value })}
                                required
                              />
                            </div>
                            <Button type="submit" data-testid="submit-edit-next-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                              Oppdater rensuke
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  <p className="text-slate-600">Uke {cleaningCycle.next_due_week}, {cleaningCycle.next_due_year}</p>
                </div>
                
                {canEditCleaning && (
                  <div className="pt-3 border-t border-slate-200 space-y-2">
                    <p className="text-xs font-medium text-slate-700 mb-2">Kontroller:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {cleaningCycle.paused_until_week ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResumeCycle}
                          data-testid="resume-cycle-button"
                          className="w-full text-xs"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Gjenoppta
                        </Button>
                      ) : (
                        <Dialog open={pauseDialog} onOpenChange={setPauseDialog}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="pause-cycle-button"
                              className="w-full text-xs"
                              disabled={cleaningCycle.is_active === false}
                            >
                              <Pause className="h-3 w-3 mr-1" />
                              Pause
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>Pause rens syklus</DialogTitle>
                              <DialogDescription>Velg hvor lenge rensen skal være pauset</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label>Antall uker</Label>
                                <Select
                                  value={pauseWeeks.toString()}
                                  onValueChange={(value) => setPauseWeeks(parseInt(value))}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="2">2 uker</SelectItem>
                                    <SelectItem value="4">4 uker</SelectItem>
                                    <SelectItem value="6">6 uker</SelectItem>
                                    <SelectItem value="8">8 uker</SelectItem>
                                    <SelectItem value="12">12 uker</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setPauseDialog(false)}
                                  className="flex-1"
                                >
                                  Avbryt
                                </Button>
                                <Button
                                  onClick={handlePauseCycle}
                                  data-testid="confirm-pause-button"
                                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                                >
                                  Pause
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleToggleActive}
                        data-testid="toggle-active-button"
                        className={`w-full text-xs ${cleaningCycle.is_active === false ? 'text-brand-600' : 'text-slate-600'}`}
                      >
                        {cleaningCycle.is_active === false ? (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Aktiver
                          </>
                        ) : (
                          <>
                            <Ban className="h-3 w-3 mr-1" />
                            Deaktiver
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-600">Ingen rens syklus registrert</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Product Dialog */}
      <Dialog open={editProductDialog} onOpenChange={setEditProductDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rediger produkt</DialogTitle>
            <DialogDescription>Oppdater produktinformasjon</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProduct} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_product_name">Produktnavn *</Label>
              <Input
                id="edit_product_name"
                data-testid="edit-product-name-input"
                value={editProductData.name}
                onChange={(e) => setEditProductData({ ...editProductData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_product_type">Type *</Label>
              <Input
                id="edit_product_type"
                data-testid="edit-product-type-input"
                value={editProductData.type}
                onChange={(e) => setEditProductData({ ...editProductData, type: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditProductDialog(false)}
                className="flex-1"
              >
                Avbryt
              </Button>
              <Button type="submit" data-testid="submit-edit-product-button" className="flex-1 btn-primary bg-slate-900 hover:bg-slate-800">
                Oppdater
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Dialog */}
      <Dialog open={editEquipmentDialog} onOpenChange={setEditEquipmentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rediger utstyr</DialogTitle>
            <DialogDescription>Oppdater utstyrsinformasjon</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditEquipment} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_equipment_name">Utstyrsnavn *</Label>
              <Input
                id="edit_equipment_name"
                data-testid="edit-equipment-name-input"
                value={editEquipmentData.name}
                onChange={(e) => setEditEquipmentData({ ...editEquipmentData, name: e.target.value })}
                required
                list="equipment-name-options"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_serial_number">Serienummer *</Label>
              <Input
                id="edit_serial_number"
                data-testid="edit-serial-number-input"
                value={editEquipmentData.serial_number}
                onChange={(e) => setEditEquipmentData({ ...editEquipmentData, serial_number: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_installation_date">Installasjonsdato *</Label>
              <Input
                id="edit_installation_date"
                data-testid="edit-installation-date-input"
                type="date"
                value={editEquipmentData.installation_date}
                onChange={(e) => setEditEquipmentData({ ...editEquipmentData, installation_date: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditEquipmentDialog(false)}
                className="flex-1"
              >
                Avbryt
              </Button>
              <Button type="submit" data-testid="submit-edit-equipment-button" className="flex-1 btn-primary bg-slate-900 hover:bg-slate-800">
                Oppdater
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="products" data-testid="products-tab">Produkter</TabsTrigger>
          <TabsTrigger value="equipment" data-testid="equipment-tab">Utstyr</TabsTrigger>
          <TabsTrigger value="installations" data-testid="installations-tab">Monteringer</TabsTrigger>
          <TabsTrigger value="services" data-testid="services-tab">Historikk</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Produkter</CardTitle>
                  <CardDescription>Øl og produkter brukt av kunden</CardDescription>
                </div>
                <Dialog open={productDialog} onOpenChange={setProductDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-product-button" size="sm" className="btn-primary bg-slate-900 hover:bg-slate-800">
                      <Plus className="mr-2 h-4 w-4" />
                      Legg til
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Legg til produkt</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="product_name">Produktnavn *</Label>
                        <Input
                          id="product_name"
                          data-testid="product-name-input"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="product_type">Type *</Label>
                        <Input
                          id="product_type"
                          data-testid="product-type-input"
                          value={newProduct.type}
                          onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value })}
                          required
                          placeholder="f.eks. Lager, IPA, Stout"
                        />
                      </div>
                      <Button type="submit" data-testid="submit-product-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                        Legg til produkt
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <p className="text-slate-600 text-center py-8">Ingen produkter registrert</p>
              ) : (
                <div className="space-y-3">
                  {products.map((product) => (
                    <div key={product.id} className="flex justify-between items-center p-3 bg-white border border-slate-200 rounded-lg">
                      <div>
                        <p className="font-semibold" style={{ color: '#111827' }}>{product.name}</p>
                        <p className="text-sm" style={{ color: '#374151' }}>{product.type}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditProductData({ id: product.id, name: product.name, type: product.type });
                            setEditProductDialog(true);
                          }}
                          className="text-slate-600 hover:text-brand-600"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteProductId(product.id)}
                          className="text-slate-600 hover:text-red-600"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="equipment">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Utstyr</CardTitle>
                  <CardDescription>Installert utstyr hos kunden</CardDescription>
                </div>
                <Dialog open={equipmentDialog} onOpenChange={setEquipmentDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-equipment-button" size="sm" className="btn-primary bg-slate-900 hover:bg-slate-800">
                      <Plus className="mr-2 h-4 w-4" />
                      Legg til
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Legg til utstyr</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateEquipment} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="equipment_name">Utstyrsnavn *</Label>
                        <Input
                          id="equipment_name"
                          data-testid="equipment-name-input"
                          value={newEquipment.name}
                          onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                          required
                          list="equipment-name-options"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="serial_number">Serienummer *</Label>
                        <Input
                          id="serial_number"
                          data-testid="serial-number-input"
                          value={newEquipment.serial_number}
                          onChange={(e) => setNewEquipment({ ...newEquipment, serial_number: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="installation_date">Installasjonsdato *</Label>
                        <Input
                          id="installation_date"
                          data-testid="installation-date-input"
                          type="date"
                          value={newEquipment.installation_date}
                          onChange={(e) => setNewEquipment({ ...newEquipment, installation_date: e.target.value })}
                          required
                        />
                      </div>
                      <Button type="submit" data-testid="submit-equipment-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                        Legg til utstyr
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {equipment.length === 0 ? (
                <p className="text-slate-600 text-center py-8">Ingen utstyr registrert</p>
              ) : (
                <div className="space-y-3">
                  {equipment.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-white border border-slate-200 rounded-lg">
                      <div>
                        <p className="font-semibold" style={{ color: '#111827' }}>{item.name}</p>
                        <p className="text-sm" style={{ color: '#374151' }}>SN: {item.serial_number}</p>
                        <p className="text-xs" style={{ color: '#4b5563' }}>
                          Installert: {format(new Date(item.installation_date), 'dd MMM yyyy', { locale: nb })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditEquipmentData({
                              id: item.id,
                              name: item.name,
                              serial_number: item.serial_number,
                              installation_date: format(new Date(item.installation_date), 'yyyy-MM-dd')
                            });
                            setEditEquipmentDialog(true);
                          }}
                          className="text-slate-600 hover:text-brand-600"
                          data-testid={`edit-equipment-${item.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteEquipmentId(item.id)}
                          className="text-slate-600 hover:text-red-600"
                          data-testid={`delete-equipment-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="installations">
          {installationDialog ? (
            <InstallationForm
              customerId={id}
              customerName={customer?.name}
              customerAddress={customer?.address}
              installationId={editInstallationId}
              onSaved={() => { setInstallationDialog(false); setEditInstallationId(null); fetchCustomerData(); }}
              onCancel={() => { setInstallationDialog(false); setEditInstallationId(null); }}
            />
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Monteringer</CardTitle>
                  <Button size="sm" onClick={() => { setEditInstallationId(null); setInstallationDialog(true); }} data-testid="new-installation-btn">
                    <Plus className="h-4 w-4 mr-1" /> Ny montering
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {installations.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4 text-center">Ingen monteringer registrert</p>
                ) : (
                  <div className="space-y-3">
                    {installations.map(inst => (
                      <div key={inst.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`installation-${inst.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Wrench className="h-4 w-4 text-slate-400 shrink-0" />
                            <span className="font-medium text-sm">{inst.date}</span>
                            <span className="text-xs text-slate-500">— {inst.technician_name}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{(inst.equipment || []).map(e => e.name).filter(Boolean).join(', ') || 'Ingen utstyr'}</p>
                          {inst.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{inst.notes}</p>}
                        </div>
                        <div className="flex gap-1.5 shrink-0 ml-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditInstallationId(inst.id); setInstallationDialog(true); }} className="h-7 px-2" data-testid={`edit-inst-${inst.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openInstallationPdf(inst.id)} className="h-7 px-2" data-testid={`pdf-inst-${inst.id}`}>
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="services">
          <Dialog open={editServiceDialog} onOpenChange={setEditServiceDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Endre service dato</DialogTitle>
                <DialogDescription>Oppdater når servicen ble utført</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateServiceDate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="service_date">Dato *</Label>
                  <Input
                    id="service_date"
                    data-testid="service-date-input"
                    type="date"
                    value={editServiceData.date}
                    onChange={(e) => setEditServiceData({ ...editServiceData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_time">Tid *</Label>
                  <Input
                    id="service_time"
                    data-testid="service-time-input"
                    type="time"
                    value={editServiceData.time}
                    onChange={(e) => setEditServiceData({ ...editServiceData, time: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditServiceDialog(false)}
                    className="flex-1"
                  >
                    Avbryt
                  </Button>
                  <Button type="submit" data-testid="submit-edit-service-button" className="flex-1 btn-primary bg-slate-900 hover:bg-slate-800">
                    Oppdater
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Card>
            <CardHeader>
              <CardTitle>Service Historikk</CardTitle>
              <CardDescription>Alle utførte servicer</CardDescription>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <p className="text-slate-600 text-center py-8">Ingen servicer registrert</p>
              ) : (
                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-brand-100 text-brand-800 capitalize">
                            {service.service_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-slate-500">
                            {format(new Date(service.completed_at), 'dd MMM yyyy, HH:mm', { locale: nb })}
                          </p>
                          {user?.role === 'admin' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const date = new Date(service.completed_at);
                                  setEditServiceData({
                                    id: service.id,
                                    date: format(date, 'yyyy-MM-dd'),
                                    time: format(date, 'HH:mm')
                                  });
                                  setEditServiceDialog(true);
                                }}
                                className="h-8 w-8 p-0 text-slate-500 hover:text-brand-600"
                                data-testid={`edit-service-${service.id}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteService(service.id)}
                                className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                                data-testid={`delete-service-${service.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="bg-white rounded p-3 mb-3 border-l-4 border-brand-500">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Utført av tekniker</p>
                        <p className="text-sm font-semibold text-slate-900">{service.technician_name}</p>
                      </div>
                      
                      {service.signature_name && (
                        <p className="text-sm text-slate-700 mb-2">
                          <span className="font-medium">Bekreftet av:</span> {service.signature_name}
                        </p>
                      )}
                      {service.notes && (
                        <p className="text-sm text-slate-600 mt-2 italic bg-white p-2 rounded border border-slate-200">{service.notes}</p>
                      )}
                      {service.signature_data && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500 mb-1">Signatur:</p>
                          <img src={service.signature_data} alt="Signatur" className="border border-slate-300 rounded bg-white p-2 h-24" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Delete Product Confirmation Dialog */}
      <AlertDialog open={!!deleteProductId} onOpenChange={(open) => !open && setDeleteProductId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette produkt?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette dette produktet? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProduct} className="bg-red-600 hover:bg-red-700">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Equipment Confirmation Dialog */}
      <AlertDialog open={!!deleteEquipmentId} onOpenChange={(open) => !open && setDeleteEquipmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette utstyr?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette dette utstyret? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEquipment} className="bg-red-600 hover:bg-red-700">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Customer Confirmation Dialog */}
      <AlertDialog open={deactivateCustomerDialog} onOpenChange={setDeactivateCustomerDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deaktiver kunde?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil deaktivere <strong>{customer?.name}</strong>?
              Kunden vil bli fjernet fra rensplanlegging og dashboard, men all data beholdes.
              Du kan aktivere kunden igjen når som helst.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-deactivate-customer"
              onClick={handleToggleCustomerActive}
              className="bg-slate-700 hover:bg-slate-800"
            >
              Deaktiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerDetail;
